import type en from '../en/users'
import type { Translation } from '../types'

/** People, roles and permissions */
const users: Translation<typeof en> = {
  "users.removed": "Usuario eliminado",
  "users.removeFailed": "No se pudo eliminar al usuario",
  "users.vpnUpdated": "Estado de la VPN actualizado",
  "users.vpnFailed": "No se pudo cambiar la VPN",
  "users.inviteSent": "Invitación enviada",
  "users.inviteSentAgain": "Se ha enviado un nuevo correo de invitación.",
  "users.resendFailed": "No se pudo reenviar la invitación",
  "users.reset2faDone": "Inicio de sesión en dos pasos restablecido",
  "users.reset2faText": "La persona cierra sesión en todas partes y lo vuelve a configurar al iniciar sesión.",
  "users.resetFailed": "Error al restablecer",
  "users.title": "Usuarios",
  "users.count": { one: "{count} miembro del equipo", other: "{count} miembros del equipo" },
  "users.invite": "Invitar usuario",
  "users.search": "Buscar usuarios",
  "users.searchPlaceholder": "Buscar por nombre o correo…",
  "users.user": "Usuario",
  "users.role": "Rol",
  "users.lastLogin": "Último inicio de sesión",
  "users.empty": "No se han encontrado usuarios",
  "users.adminBadge": "Admin",
  "users.twoFactorOn": "El inicio de sesión en dos pasos está activado",
  "users.never": "Nunca",
  "users.resend": "Reenviar el correo de invitación",
  "users.suspendVpn": "Suspender el acceso VPN",
  "users.enableVpn": "Volver a activar el acceso VPN",
  "users.suspendVpnConfirm": "¿Suspender la VPN de «{name}»?",
  "users.enableVpnConfirm": "¿Volver a activar la VPN de «{name}»?",
  "users.reset2fa": "Restablecer el inicio de sesión en dos pasos",
  "users.reset2faFor": "Restablecer el inicio de sesión en dos pasos de {name}",
  "users.reset2faConfirm": "¿Restablecer el inicio de sesión en dos pasos de «{name}»? La persona cierra sesión en todas partes y lo vuelve a configurar al iniciar sesión.",
  "users.editAccess": "Editar acceso",
  "users.removeNamed": "Eliminar a {name}",
  "users.removeConfirm": "¿Eliminar al usuario «{name}»?",
  "users.updated": "Usuario actualizado",
  "users.addedNoMail": "Usuario añadido, pero no se pudo enviar el correo de invitación",
  "users.addedNoMailText": "Compruebe la configuración del correo y use después «Reenviar el correo de invitación».",
  "users.siteAccess": "Acceso a sitios",
  "users.allSites": "Todos los sitios (incluidos los que se añadan después)",
  "users.noSites": "Todavía no hay sitios",
  "users.noneSelected": "No se ha elegido ningún sitio: este usuario no verá ninguno.",
  "users.updateFailed": "No se pudo actualizar al usuario",
  "users.editTitle": "Editar a {name}",
  "users.noRole": "Sin rol",
  "users.noRoleCustom": "Sin rol (permisos propios)",
  "users.companyAdmin": "Administrador de la empresa (acceso completo)",
  "users.save": "Guardar",
  "users.inviteFailed": "No se pudo invitar al usuario",
  "users.inviteTitle": "Invitar a un miembro del equipo",
  "users.fullName": "Nombre completo *",
  "users.fullNamePlaceholder": "María García",
  "users.email": "Correo electrónico *",
  "users.emailPlaceholder": "maria@empresa.es",
  "users.inviteInfo": "Se envía un correo de invitación con un enlace para establecer la contraseña. La cuenta VPN se crea automáticamente.",
  "users.sendInvite": "Enviar invitación",
  "users.roles.deleted": "Rol eliminado",
  "users.roles.deleteFailed": "No se pudo eliminar el rol",
  "users.roles.title": "Roles",
  "users.roles.subtitle": "Conjuntos de permisos para los miembros de su equipo",
  "users.roles.create": "Crear rol",
  "users.roles.system": "SISTEMA",
  "users.roles.editNamed": "Editar {name}",
  "users.roles.deleteNamed": "Eliminar {name}",
  "users.roles.deleteConfirm": "¿Eliminar el rol «{name}»?",
  "users.roles.empty": "Todavía no hay roles: cree uno para asignarlo a los miembros del equipo",
  "users.roles.noPermissions": "Sin permisos asignados",
  "users.roles.updated": "Rol actualizado",
  "users.roles.updateFailed": "No se pudo actualizar el rol",
  "users.roles.savePermissions": "Guardar permisos",
  "users.roles.created": "Rol creado",
  "users.roles.createFailed": "No se pudo crear el rol",
  "users.roles.name": "Nombre del rol *",
  "users.roles.namePlaceholder": "Ingeniero de redes",
  "users.roles.description": "Descripción",
  "users.roles.descriptionPlaceholder": "Descripción opcional",
  "users.roles.permissions": "Permisos",
  "users.roles.selected": { one: "{count} permiso elegido", other: "{count} permisos elegidos" },
  "users.roles.hoverHint": "Señale un permiso para ver qué permite",
  "users.group.sites": "Sitios",
  "users.group.monitoring": "Monitorización",
  "users.group.templates": "Plantillas",
  "users.group.users": "Usuarios",
  "users.group.vpn": "VPN",
  "users.group.company": "Empresa",
  "users.group.api": "API",
  "users.permission.SITE_VIEW": "Ver",
  "users.permission.SITE_CREATE": "Crear",
  "users.permission.SITE_EDIT": "Editar",
  "users.permission.SITE_DELETE": "Eliminar",
  "users.permission.MONITORING_VIEW": "Ver",
  "users.permission.TEMPLATE_VIEW": "Ver",
  "users.permission.TEMPLATE_CREATE": "Crear",
  "users.permission.TEMPLATE_EDIT": "Editar",
  "users.permission.TEMPLATE_DELETE": "Eliminar",
  "users.permission.TEMPLATE_APPLY": "Aplicar",
  "users.permission.USER_VIEW": "Ver",
  "users.permission.USER_CREATE": "Invitar",
  "users.permission.USER_EDIT": "Editar",
  "users.permission.USER_DELETE": "Quitar",
  "users.permission.VPN_ACCESS": "Acceso",
  "users.permission.VPN_VIEW": "Ver",
  "users.permission.VPN_MANAGE": "Gestionar",
  "users.permission.COMPANY_BRANDING": "Imagen de marca",
  "users.permission.COMPANY_SETTINGS": "Configuración",
  "users.permission.LICENSE_VIEW": "Ver licencia",
  "users.permission.LICENSE_MANAGE": "Gestionar licencia",
  "users.permission.APIKEY_VIEW": "Ver",
  "users.permission.APIKEY_CREATE": "Crear",
  "users.permission.APIKEY_DELETE": "Revocar",
  "users.permissionDesc.SITE_VIEW": "Ver la lista de sitios y sus datos básicos",
  "users.permissionDesc.SITE_CREATE": "Añadir sitios nuevos",
  "users.permissionDesc.SITE_EDIT": "Cambiar la configuración y la clave de API de un sitio",
  "users.permissionDesc.SITE_DELETE": "Eliminar sitios definitivamente",
  "users.permissionDesc.MONITORING_VIEW": "Ver datos en directo, dispositivos y clientes",
  "users.permissionDesc.TEMPLATE_VIEW": "Explorar la biblioteca de plantillas",
  "users.permissionDesc.TEMPLATE_CREATE": "Crear plantillas nuevas",
  "users.permissionDesc.TEMPLATE_EDIT": "Cambiar plantillas existentes",
  "users.permissionDesc.TEMPLATE_DELETE": "Eliminar plantillas",
  "users.permissionDesc.TEMPLATE_APPLY": "Aplicar plantillas a sitios",
  "users.permissionDesc.USER_VIEW": "Ver la lista de miembros del equipo",
  "users.permissionDesc.USER_CREATE": "Invitar a miembros nuevos",
  "users.permissionDesc.USER_EDIT": "Cambiar roles y estado",
  "users.permissionDesc.USER_DELETE": "Quitar miembros de la empresa",
  "users.permissionDesc.VPN_ACCESS": "El miembro recibe una cuenta VPN",
  "users.permissionDesc.VPN_VIEW": "Ver la página de VPN y el estado de las cuentas",
  "users.permissionDesc.VPN_MANAGE": "Crear, suspender y renovar cuentas VPN",
  "users.permissionDesc.COMPANY_BRANDING": "Cambiar el logotipo, los colores y el aspecto",
  "users.permissionDesc.COMPANY_SETTINGS": "Gestionar la configuración y el servidor de correo de la empresa",
  "users.permissionDesc.LICENSE_VIEW": "Ver el estado y los límites de la licencia",
  "users.permissionDesc.LICENSE_MANAGE": "Renovar y cambiar la licencia",
  "users.permissionDesc.APIKEY_VIEW": "Ver la lista de claves de API",
  "users.permissionDesc.APIKEY_CREATE": "Crear claves de API nuevas",
  "users.permissionDesc.APIKEY_DELETE": "Revocar claves de API",
  "users.group.rental": "Alquiler",
  "users.permission.RENTAL_VIEW": "Ver",
  "users.permission.RENTAL_PLAN": "Planificar",
  "users.permission.STOCK_MANAGE": "Gestionar inventario",
  "users.permission.PURCHASE_APPROVE": "Aprobar y enviar compras",
  "users.permissionDesc.RENTAL_VIEW": "Ver inventario, clientes, eventos, planificación y precios de alquiler",
  "users.permissionDesc.RENTAL_PLAN": "Gestionar clientes y eventos: líneas, precios, descuentos y estado",
  "users.permissionDesc.STOCK_MANAGE": "Gestionar artículos, unidades, maletas, almacenes, traslados, proveedores, subalquileres y pedidos de compra, y ver costes",
  "users.permissionDesc.PURCHASE_APPROVE": "Aprobar pedidos de compra y solicitudes de subalquiler, lo que los envía al proveedor",
  "users.permission.MIKROTIK_PROVISION": "Aprovisionar unidades MikroTik",
  "users.permissionDesc.MIKROTIK_PROVISION": "Crear o actualizar un sitio MikroTik por su número de serie, emitir su certificado VPN y recibir su configuración",
  "users.permission.TELTONIKA_PROVISION": "Aprovisionar routers Teltonika",
  "users.permissionDesc.TELTONIKA_PROVISION": "Crear o actualizar un sitio Teltonika por su número de serie, emitir su certificado VPN y recibir su configuración",
  "users.permission.CLIENT_ACCOUNTS_MANAGE": "Gestionar cuentas de clientes",
  "users.permissionDesc.CLIENT_ACCOUNTS_MANAGE": "Invitar, cambiar y eliminar a las personas de un cliente, y abrir \"Ver como cliente\" en un evento. Solo para miembros con acceso a todos los sitios",
}

export default users
